import {
  textUI,
  radioUI,
  radioSchema,
  currentOrPastDateUI,
  titleUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import VaDateField from 'platform/forms-system/src/js/web-component-fields/VaDateField';
import VaMemorableDateField from 'platform/forms-system/src/js/web-component-fields/VaMemorableDateField';
import {
  currentYear,
  parseISODate,
} from 'platform/forms-system/src/js/helpers';
import { isValidDate } from 'platform/forms-system/src/js/utilities/validations';
import {
  applicationInfoFields,
  BRANCH_OF_SERVICE,
  FORM_21_4502,
} from '../definitions/constants';

const { APPLICATION_INFORMATION: AI } = FORM_21_4502;

const isCompleteValidDate = value => {
  const { day, month, year } = parseISODate(value);

  return (
    [day, month, year].every(Boolean) &&
    Number(year) >= 1900 &&
    Number(year) <= currentYear &&
    isValidDate(day, month, year)
  );
};

const formatDateLong = value => {
  const { day, month, year } = parseISODate(value);

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
  ).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const validateDateOfEntry = (errors, value, formData) => {
  if (!formData?.form214502ServiceDateValidation) {
    return;
  }
  const { day, month, year } = parseISODate(value);
  const hasAnyDatePart = [day, month, year].some(Boolean);
  const hasCompleteDate = [day, month, year].every(Boolean);

  if (!value || !hasAnyDatePart) {
    errors.addError(AI.ERROR_DATE_ENTRY_REQUIRED);
    return;
  }

  if (!hasCompleteDate) {
    errors.addError(AI.ERROR_DATE_ENTRY_INCOMPLETE);
    return;
  }

  if (
    Number(year) < 1900 ||
    Number(year) > currentYear ||
    !isValidDate(day, month, year)
  ) {
    errors.addError(AI.ERROR_DATE_ENTRY_INVALID);
    return;
  }

  const dateOfRelease =
    formData?.[applicationInfoFields.parentObject]?.[
      applicationInfoFields.dateOfRelease
    ];

  if (isCompleteValidDate(dateOfRelease) && value > dateOfRelease) {
    errors.addError(
      AI.ERROR_DATE_ENTRY_AFTER_RELEASE(formatDateLong(dateOfRelease)),
    );
  }
};

const buildApplicationInformationPage = ({
  DateField,
  monthSelect = false,
}) => {
  const dateUI = currentOrPastDateUI({
    title: AI.DATE_OF_ENTRY,
    required: () => true,
    errorMessages: {
      pattern: AI.ERROR_DATE_ENTRY_INVALID,
      required: AI.ERROR_DATE_ENTRY_REQUIRED,
    },
  });

  return {
    uiSchema: {
      'ui:options': {
        preserveHiddenData: true,
        forceDivWrapper: true,
      },
      ...titleUI(AI.TITLE, AI.PAGE_DESCRIPTION),
      [applicationInfoFields.parentObject]: {
        [applicationInfoFields.branchOfService]: radioUI({
          title: AI.BRANCH_OF_SERVICE,
          labels: BRANCH_OF_SERVICE,
          required: () => true,
          errorMessages: {
            required: AI.ERROR_BRANCH_OF_SERVICE,
          },
        }),
        [applicationInfoFields.placeOfEntry]: textUI({
          title: AI.PLACE_OF_ENTRY,
          required: () => true,
          errorMessages: {
            required: AI.ERROR_PLACE_OF_ENTRY,
          },
        }),
        [applicationInfoFields.dateOfEntry]: {
          ...dateUI,
          'ui:options': {
            ...(dateUI['ui:options'] || {}),
            ...(monthSelect ? { monthSelect: true } : {}),
          },
          'ui:webComponentField': DateField,
          'ui:errorMessages': {
            pattern: AI.ERROR_DATE_ENTRY_INVALID,
            required: AI.ERROR_DATE_ENTRY_REQUIRED,
          },
          'ui:validations': [validateDateOfEntry],
        },
      },
    },
    schema: {
      type: 'object',
      properties: {
        [applicationInfoFields.parentObject]: {
          type: 'object',
          properties: {
            [applicationInfoFields.branchOfService]: radioSchema(
              Object.keys(BRANCH_OF_SERVICE),
            ),
            [applicationInfoFields.placeOfEntry]: {
              type: 'string',
              maxLength: 100,
            },
            [applicationInfoFields.dateOfEntry]: { type: 'string' },
          },
          required: [
            applicationInfoFields.branchOfService,
            applicationInfoFields.placeOfEntry,
            applicationInfoFields.dateOfEntry,
          ],
        },
      },
    },
  };
};
export const applicationInformationNewFlow = buildApplicationInformationPage({
  DateField: VaMemorableDateField,
  monthSelect: true,
});
export default buildApplicationInformationPage({
  DateField: VaDateField,
});
